/**
 * O "próximo passo" dos Primeiros Passos — função pura, no navegador (spec #387, seção 4).
 *
 * O servidor não decide qual é o próximo clique: ele só sabe o estado. Quem sabe se a pessoa
 * consegue dar o passo é o manifesto dela, e por isso a escolha mora aqui. A página e o aviso
 * da página inicial usam esta mesma função sobre o mesmo payload, e por isso não discordam.
 */

import { avaliar } from '../../authorization/pode';
import type { Manifesto } from '../../types/manifesto';
import type { OnboardingItem, OnboardingStatus, TrackKey } from '../../types/onboarding';
import { acaoDoItem, ITENS, type Destino, type ItemTexto } from './textos';

export type ManifestoDeAcesso = Pick<Manifesto, 'menu' | 'modulos' | 'permissoes' | 'perspectiva'>;

export interface ProximoPasso {
  trilha: TrackKey;
  item: OnboardingItem;
  texto: ItemTexto;
  acao: Destino;
}

/** Estados que entram na conta de "N de M": informativo e opcional não são passo de ninguém. */
const CONTA_NO_PROGRESSO = new Set(['done', 'warn', 'pending', 'locked']);

/**
 * O primeiro item pendente que a pessoa consegue resolver, na ordem das trilhas. Pula a trilha
 * bloqueada ou opcional, o item que não é pendente, o que a tabela não leva a lugar nenhum e o
 * que o manifesto atual não libera. Sem candidato, devolve `null`: o cartão diz a quem pedir.
 */
export function proximoPasso(
  status: OnboardingStatus,
  manifesto: ManifestoDeAcesso | null
): ProximoPasso | null {
  if (!manifesto) return null;

  for (const trilha of status.tracks) {
    if (trilha.blocked || trilha.optional) continue;

    for (const item of trilha.items) {
      if (item.state !== 'pending') continue;

      const texto = ITENS[item.key];
      const acao = texto ? acaoDoItem(texto, item) : undefined;
      if (!texto || !acao) continue;
      if (!avaliar(acao.rota, manifesto, acao.exige).liberado) continue;

      return { trilha: trilha.key, item, texto, acao };
    }
  }

  return null;
}

/** Quantos itens estão concluídos de quantos contam, fora das trilhas opcionais. */
export function progresso(status: OnboardingStatus): { feitos: number; total: number } {
  let feitos = 0;
  let total = 0;

  for (const trilha of status.tracks) {
    if (trilha.optional) continue;
    for (const item of trilha.items) {
      if (!CONTA_NO_PROGRESSO.has(item.state)) continue;
      total += 1;
      if (item.state === 'done' || item.state === 'warn') feitos += 1;
    }
  }

  return { feitos, total };
}

/** Há trilha e nada pendente fora das opcionais: a tela vira consulta e o aviso da Home some. */
export function concluido(status: OnboardingStatus): boolean {
  if (status.tracks.length === 0) return false;

  return !status.tracks.some(
    (trilha) => !trilha.optional && trilha.items.some((item) => item.state === 'pending')
  );
}
